import React, { useMemo } from "react";
import Select, { SingleValue } from "react-select";
import { City } from "country-state-city";

interface Option {
  value: string;
  label: string;
}

interface CitySelectProps {
  value: string;
  onChange: (city: string) => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
  name?: string;
  id?: string;
}

export const CitySelect: React.FC<CitySelectProps> = ({
  value,
  onChange,
  placeholder = "Select city",
  className,
  name,
  id,
}) => {
  const options = useMemo(() => {
    const rawCities = City.getCitiesOfCountry("IN") || [];
    const citySet = new Set<string>();

    rawCities.forEach((c) => {
      if (c.name) citySet.add(c.name);
    });

    // Ensure common major cities are present
    const topCities = [
      "Bangalore",
      "Bengaluru",
      "Mumbai",
      "Delhi",
      "Hyderabad",
      "Chennai",
      "Kolkata",
      "Pune",
      "Ahmedabad",
      "Jaipur",
    ];
    topCities.forEach((city) => citySet.add(city));

    return Array.from(citySet)
      .sort((a, b) => a.localeCompare(b))
      .map((cityName) => ({ value: cityName, label: cityName }));
  }, []);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    return (
      options.find(
        (opt) => opt.value.toLowerCase() === value.trim().toLowerCase()
      ) || { value, label: value }
    );
  }, [value, options]);

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      minHeight: "42px",
      borderRadius: "8px",
      borderColor: state.isFocused ? "#0d9488" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 1px #0d9488" : "none",
      "&:hover": {
        borderColor: state.isFocused ? "#0d9488" : "#9ca3af",
      },
      fontSize: "14px",
      backgroundColor: "#ffffff",
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999,
      fontSize: "14px",
    }),
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: 9999,
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#0d9488"
        : state.isFocused
        ? "#e6f4f1"
        : "#ffffff",
      color: state.isSelected ? "#ffffff" : "#1f2937",
      cursor: "pointer",
      "&:active": {
        backgroundColor: "#0d9488",
        color: "#ffffff",
      },
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: "#9ca3af",
      fontSize: "14px",
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: "#1f2937",
      fontSize: "14px",
    }),
  };

  return (
    <Select<Option>
      id={id}
      name={name}
      className={className}
      options={options}
      value={selectedOption}
      onChange={(newValue: SingleValue<Option>) => {
        onChange(newValue ? newValue.value : "");
      }}
      placeholder={placeholder}
      isClearable
      isSearchable
      styles={customStyles}
      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
    />
  );
};

export default CitySelect;
